<template>
  <v-container fluid>
    <v-row justify="end">
      <v-col cols="12" align="center" justify="center">
        <v-sheet class="pa-2">
          <v-row>
            <v-col cols="12">
              <h1>Onboarding</h1>
            </v-col>
          </v-row>
          <v-row v-if="loading">
            <v-col cols="12" align="center" justify="center">
              <v-progress-circular
                indeterminate
                color="secondary"
                size="70"
                class="my-4"
              ></v-progress-circular>
            </v-col>
          </v-row>
          <div v-else>
            <v-row>
              <v-col cols="3">
                <v-card :loading="loading">
                  <v-toolbar dark color="secondary">
                    <v-toolbar-title>
                      Number of clients being onboarded
                    </v-toolbar-title>
                  </v-toolbar>
                  <v-card-text v-if="userSubscriptions.edges">
                    <v-spacer>
                      <v-avatar
                        color="font-color"
                        class="white--text font-24"
                        size="100"
                      >
                        {{ userSubscriptions.edges.length }}
                      </v-avatar>
                    </v-spacer>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="3">
                <v-card :loading="loading">
                  <v-toolbar dark color="secondary">
                    <v-toolbar-title>
                      Number of clients without a coach</v-toolbar-title
                    >
                  </v-toolbar>
                  <v-card-text v-if="userSubscriptions.edges">
                    <v-spacer>
                      <v-avatar
                        color="font-color"
                        class="white--text font-24"
                        size="100"
                      >
                        {{ clientsWithNoCoach }}
                      </v-avatar>
                    </v-spacer>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="3">
                <v-card :loading="loading">
                  <v-toolbar dark color="secondary">
                    <v-toolbar-title>
                      Number of clients without a support owner
                    </v-toolbar-title>
                  </v-toolbar>
                  <v-card-text v-if="userSubscriptions.edges">
                    <v-spacer>
                      <v-avatar
                        color="font-color"
                        class="white--text font-24"
                        size="100"
                      >
                        {{ clientsWithNoSupport }}
                      </v-avatar>
                    </v-spacer>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="3">
                <v-card :loading="loading">
                  <v-toolbar dark color="secondary">
                    <v-toolbar-title>
                      Number of clients without start date
                    </v-toolbar-title>
                  </v-toolbar>
                  <v-card-text v-if="userSubscriptions.edges">
                    <v-spacer>
                      <v-avatar
                        color="font-color"
                        class="white--text font-24"
                        size="100"
                      >
                        {{ clientsWithNoStartDate }}
                      </v-avatar>
                    </v-spacer>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-tabs v-model="tab" color="secondary" fixed-tabs>
                  <v-tabs-slider color="secondary"></v-tabs-slider>
                  <v-tab key="Support">Support</v-tab>
                  <v-tab key="Coaching">Coaching</v-tab>
                  <v-tab key="Client">Client</v-tab>
                  <v-tab key="onboaringTime">Average time to onboard</v-tab>
                </v-tabs>

                <v-tabs-items v-model="tab">
                  <v-tab-item key="Support">
                    <v-row>
                      <v-col cols="12" class="py-2">
                        <h2>Support Owner Summary</h2>
                        <v-simple-table :loading="loading">
                          <thead>
                            <tr>
                              <th class="text-left">Name</th>
                              <th class="text-left">Number of clients</th>
                              <th class="text-left">
                                # Programs without a start date
                              </th>
                              <th class="text-left">
                                # Message not sent by user
                              </th>
                              <th class="text-left">
                                # Message not sent by coach
                              </th>
                              <th class="text-left">
                                # Medical form not submitted
                              </th>
                              <th class="text-left">
                                # Initial assessment not booked
                              </th>
                              <th class="text-left">
                                # Initial assessment not completed
                              </th>
                              <th class="text-left">
                                # With no nutrition plan
                              </th>
                              <th class="text-left"># With no plan set</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(value, name) in onboardingOwnerSummary"
                              :key="name"
                            >
                              <td>
                                <router-link
                                  class="
                                    subtitle-1
                                    grey-navy--text
                                    font-weight-bold
                                  "
                                  style="text-decoration: none"
                                  :to="
                                    '/operations/onboarding?role=support&userId=' +
                                    name
                                  "
                                >
                                  {{ value.supportFullName }}
                                </router-link>
                              </td>
                              <td>{{ value.total }}</td>
                              <td>{{ value.programWithoutStartDate }}</td>
                              <td>{{ value.noMessageSentByUser }}</td>
                              <td>{{ value.noMessageSentByCoach }}</td>
                              <td>{{ value.noMedicalForm }}</td>
                              <td>{{ value.noInitialAssessmentBooked }}</td>
                              <td>{{ value.noInitialAssessmentCompleted }}</td>
                              <td>{{ value.noNutritionPlan }}</td>
                              <td>{{ value.noPlanSet }}</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                  <v-tab-item key="Coaching">
                    <v-row>
                      <v-col cols="12">
                        <h2>Coach Summary</h2>
                        <v-simple-table :loading="loading">
                          <thead>
                            <tr>
                              <th class="text-left">Name</th>
                              <th class="text-left">Number of clients</th>
                              <th class="text-left">
                                # Programs without a start date
                              </th>
                              <th class="text-left">
                                # Message not sent by user
                              </th>
                              <th class="text-left">
                                # Message not sent by coach
                              </th>
                              <th class="text-left">
                                # Medical form not submitted
                              </th>
                              <th class="text-left">
                                # Initial assessment not booked
                              </th>
                              <th class="text-left">
                                # Initial assessment not completed
                              </th>
                              <th class="text-left">
                                # With no nutrition plan
                              </th>
                              <th class="text-left"># With no plan set</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(value, name) in coachSummary"
                              :key="name"
                            >
                              <td>
                                <router-link
                                  class="
                                    subtitle-1
                                    grey-navy--text
                                    font-weight-bold
                                  "
                                  :to="
                                    '/operations/onboarding?role=coach&userId=' +
                                    name
                                  "
                                >
                                  {{ value.coachFullName }}
                                </router-link>
                              </td>
                              <td>{{ value.total }}</td>
                              <td>{{ value.programWithoutStartDate }}</td>
                              <td>{{ value.noMessageSentByUser }}</td>
                              <td>{{ value.noMessageSentByCoach }}</td>
                              <td>{{ value.noMedicalForm }}</td>
                              <td>{{ value.noInitialAssessmentBooked }}</td>
                              <td>{{ value.noInitialAssessmentCompleted }}</td>
                              <td>{{ value.noNutritionPlan }}</td>
                              <td>{{ value.noPlanSet }}</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                  <v-tab-item key="Client">
                    <v-row align="center" justify="center">
                      <v-col cols="12">
                        <h2>Client Risk Summary</h2>
                        <v-simple-table>
                          <thead>
                            <tr>
                              <th class="text-left">Name</th>
                              <th class="text-left">Days since sign up</th>
                              <th class="text-left">Last Login</th>
                              <th class="text-left">Phone</th>
                              <th class="text-left">Program Start Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="subscription in riskClientSummary"
                              :key="subscription.id"
                            >
                              <td>
                                <router-link
                                  class="
                                    subtitle-1
                                    grey-navy--text
                                    font-weight-bold
                                  "
                                  :to="'/clients/' + subscription.user.id"
                                >
                                  {{ getName(subscription.user) }}
                                </router-link>
                              </td>
                              <td>
                                {{
                                  $moment().diff(
                                    $moment(subscription.startDate),
                                    "days"
                                  )
                                }}
                              </td>
                              <td>
                                {{
                                  subscription.user.lastLogin
                                    | moment("YYYY-MM-DD HH:MM")
                                }}
                              </td>
                              <td>{{ subscription.user.userProfile.phone }}</td>
                              <td>{{ subscription.programStartDate }}</td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                  <v-tab-item key="onboaringTime">
                    <v-row align="center" justify="center">
                      <v-col cols="12">
                        <h2>Average time to onboard for the last 5 months</h2>
                        <v-simple-table>
                          <thead>
                            <tr>
                              <th class="text-left">Coach</th>
                              <th class="text-left">Average</th>
                              <th class="text-left">Max</th>
                              <th class="text-left">Min</th>
                              <th class="text-left">Sample</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(item, index) in coachOnboardingSummary"
                              :key="index"
                            >
                              <td>
                                {{item.coach.firstName}} {{item.coach.lastName}}
                              </td>
                              <td>
                                {{parseFloat(item.average).toFixed(2)}} days
                              </td>
                              <td>
                                {{item.max}} days
                              </td>
                              <td>
                                {{item.min}} days
                              </td>
                              <td>
                                {{item.total}} subscriptions
                              </td>
                            </tr>
                          </tbody>
                        </v-simple-table>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
            </v-row>
          </div>
        </v-sheet>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ONBOARDING_QUERY, COACHING_ONBOARDING_SUMMARY_QUERY } from "@/constants/graphql/operations/onboarding";
import { getUserName } from "@/lib/username";
import { fancy_date } from "@/lib/fancy_date";

export default {
  name: "OnboardingSummary",
  data() {
    return {
      userSubscriptions: [],
      editCell: null,
      loading: true,
      onboardingOwnerSummary: null,
      coachSummary: null,
      clientsWithNoCoach: 0,
      clientsWithNoSupport: 0,
      clientsWithNoStartDate: 0,
      tab: "Support",
      riskClientSummary: [],
      coachOnboardingSummary: []
    };
  },
  methods: {
    fancyDate(date) {
      return fancy_date(date);
    },
    getName(user) {
      return getUserName(user);
    },
    addToCoaching(item) {
      if (item.node.coach.id in this.coachSummary) {
        let exisitingRow = this.coachSummary[item.node.coach.id];
        exisitingRow["total"] = exisitingRow["total"] + 1;
        exisitingRow["programWithoutStartDate"] =
          exisitingRow["programWithoutStartDate"] +
          (item.node.programStartDate ? 0 : 1);
        exisitingRow["noMedicalForm"] =
          exisitingRow["noMedicalForm"] +
          (item.node.submittedMedicalForm ? 0 : 1);
        exisitingRow["noMessageSentByUser"] =
          exisitingRow["noMessageSentByUser"] +
          (item.node.messageSentByUser ? 0 : 1);
        exisitingRow["noMessageSentByCoach"] =
          exisitingRow["noMessageSentByCoach"] +
          (item.node.messageSentByCoach ? 0 : 1);
        exisitingRow["noPlanSet"] =
          exisitingRow["noPlanSet"] +
          (item.node.onboardingProgram != null &&
          item.node.onboardingProgram.nextProgram
            ? 0
            : 1);
        exisitingRow["noNutritionPlan"] =
          exisitingRow["noNutritionPlan"] +
          (item.node.onboardingProgram != null &&
          item.node.onboardingProgram.nutritionPlan
            ? 0
            : 1);
        exisitingRow["noInitialAssessmentBooked"] =
          exisitingRow["noInitialAssessmentBooked"] +
          (item.node.initialAssessment != null &&
          item.node.initialAssessment.requiredDate
            ? 0
            : 1);
        exisitingRow["noInitialAssessmentCompleted"] =
          exisitingRow["noInitialAssessmentCompleted"] +
          (item.node.initialAssessment != null &&
          item.node.initialAssessment.assessmentDate
            ? 0
            : 1);

        this.coachSummary[item.node.coach.id] = exisitingRow;
      } else {
        let newRow = {
          coachFullName: getUserName(item.node.coach),
          total: 1,
          programWithoutStartDate: item.node.programStartDate ? 0 : 1,
          noMedicalForm: item.node.submittedMedicalForm ? 0 : 1,
          noMessageSentByUser: item.node.messageSentByUser ? 0 : 1,
          noMessageSentByCoach: item.node.messageSentByCoach ? 0 : 1,
          noPlanSet:
            item.node.onboardingProgram != null &&
            item.node.onboardingProgram.nextProgram
              ? 0
              : 1,
          noNutritionPlan:
            item.node.onboardingProgram != null &&
            item.node.onboardingProgram.nutritionPlan
              ? 0
              : 1,
          noInitialAssessmentBooked:
            item.node.initialAssessment != null &&
            item.node.initialAssessment.requiredDate
              ? 0
              : 1,
          noInitialAssessmentCompleted:
            item.node.initialAssessment != null &&
            item.node.initialAssessment.assessmentDate
              ? 0
              : 1,
        };
        this.coachSummary[item.node.coach.id] = newRow;
      }
    },
    addToSupportOwnerSummary(item) {
      if (item.node.supportOwner.id in this.onboardingOwnerSummary) {
        let exisitingRow =
          this.onboardingOwnerSummary[item.node.supportOwner.id];
        exisitingRow["total"] = exisitingRow["total"] + 1;
        exisitingRow["programWithoutStartDate"] =
          exisitingRow["programWithoutStartDate"] +
          (item.node.programStartDate ? 0 : 1);
        exisitingRow["noMedicalForm"] =
          exisitingRow["noMedicalForm"] +
          (item.node.submittedMedicalForm ? 0 : 1);
        exisitingRow["noMessageSentByUser"] =
          exisitingRow["noMessageSentByUser"] +
          (item.node.messageSentByUser ? 0 : 1);
        exisitingRow["noMessageSentByCoach"] =
          exisitingRow["noMessageSentByCoach"] +
          (item.node.messageSentByCoach ? 0 : 1);
        exisitingRow["noPlanSet"] =
          exisitingRow["noPlanSet"] +
          (item.node.onboardingProgram != null &&
          item.node.onboardingProgram.nextProgram
            ? 0
            : 1);
        exisitingRow["noNutritionPlan"] =
          exisitingRow["noNutritionPlan"] +
          (item.node.onboardingProgram != null &&
          item.node.onboardingProgram.nutritionPlan
            ? 0
            : 1);
        exisitingRow["noInitialAssessmentBooked"] =
          exisitingRow["noInitialAssessmentBooked"] +
          (item.node.initialAssessment != null &&
          item.node.initialAssessment.requiredDate
            ? 0
            : 1);
        exisitingRow["noInitialAssessmentCompleted"] =
          exisitingRow["noInitialAssessmentCompleted"] +
          (item.node.initialAssessment != null &&
          item.node.initialAssessment.assessmentDate
            ? 0
            : 1);

        this.onboardingOwnerSummary[item.node.supportOwner.id] = exisitingRow;
      } else {
        let newRow = {
          supportFullName: getUserName(item.node.supportOwner),
          total: 1,
          programWithoutStartDate: item.node.programStartDate ? 0 : 1,
          noMedicalForm: item.node.submittedMedicalForm ? 0 : 1,
          noMessageSentByUser: item.node.messageSentByUser ? 0 : 1,
          noMessageSentByCoach: item.node.messageSentByCoach ? 0 : 1,
          noPlanSet:
            item.node.onboardingProgram != null &&
            item.node.onboardingProgram.nextProgram
              ? 0
              : 1,
          noNutritionPlan:
            item.node.onboardingProgram != null &&
            item.node.onboardingProgram.nutritionPlan
              ? 0
              : 1,
          noInitialAssessmentBooked:
            item.node.initialAssessment != null &&
            item.node.initialAssessment.requiredDate
              ? 0
              : 1,
          noInitialAssessmentCompleted:
            item.node.initialAssessment != null &&
            item.node.initialAssessment.assessmentDate
              ? 0
              : 1,
        };
        this.onboardingOwnerSummary[item.node.supportOwner.id] = newRow;
      }
    },
  },
  apollo: {
    userSubscriptions: {
      query: ONBOARDING_QUERY,
      variables() {
        this.loading = true;
        let input = {
          stage: "onboarding",
          productType: "one-on-one",
        };
        return input;
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.onboardingOwnerSummary = {};
          this.coachSummary = {};
          this.riskClientSummary = [];
          this.clientsWithNoCoach = 0;
          this.clientsWithNoSupport = 0;
          this.clientsWithNoStartDate = 0;

          response.data.userSubscriptions.edges.forEach((item) => {
            if (item.node.supportOwner) {
              this.addToSupportOwnerSummary(item);
            } else {
              this.clientsWithNoSupport += 1;
            }
            if (item.node.coach) {
              this.addToCoaching(item);
            } else {
              this.clientsWithNoCoach += 1;
            }
            if (item.node.programStartDate == null) {
              this.clientsWithNoStartDate += 1;
            }
            if (
              this.$moment() >
                this.$moment(item.node.startDate).add(7, "days") ||
              item.node.programStartDate == null ||
              item.node.user.userProfile.phone == null
            ) {
              this.riskClientSummary.push(item.node);
            }
          });

          this.loading = false;
        }
        if (
          response.error &&
          response.error.gqlError?.message == "Permission Denied."
        ) {
          this.$router.push("/no-access");
        }
      },
    },
    coachOnboardingSummary: {
      query: COACHING_ONBOARDING_SUMMARY_QUERY,
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
      
          this.loading = false;
        }
        if (
          response.error &&
          response.error.gqlError?.message == "Permission Denied."
        ) {
          this.$router.push("/no-access");
        }
      },
    },
  },
};
</script>


<style scoped>
.font-24 {
  font-size: 24px;
}
</style>
